import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth';
import { useEmployees } from '@/contexts/employees/EmployeesContext';
import { toast } from 'sonner';
import { UserRole, DepartmentMetric } from '@/types/enums';
import { EmployeeStatus, ResidencyType } from '@/contexts/employees/EmployeesContext';

// Type definition for Employee - matches User interface exactly
interface Employee {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  department: string;
  position: string;
  status: string;
  shift: string;
  residency: string;
  workerType?: string; // Make optional to match User type
  employee_id?: string; // Make optional to match User type
  is_duty_roster?: boolean; // NEW: Duty roster flag
  group_id?: string; // NEW: Group assignment for duty roster employees
  group_name?: string; // NEW: Group name for display
}

interface Department {
  id: string;
  name: string;
  company_id: string;
}

export function useEmployeeManagement() {
  const { user, isLoadingAuth } = useAuth();

  // ✅ NEW: Centralized state management
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(true);
  const [employeesError, setEmployeesError] = useState<string | null>(null);

  // State management
  const [activeTab, setActiveTab] = useState<string>(DepartmentMetric.EMPLOYEES);
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isOvertimeDialogOpen, setIsOvertimeDialogOpen] = useState(false);
  const [isChangeDeptDialogOpen, setIsChangeDeptDialogOpen] = useState(false);
  const [isChangeStatusDialogOpen, setIsChangeStatusDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<null | Employee>(null);
  const [isNewUserDialogOpen, setIsNewUserDialogOpen] = useState(false);
  
  // Department state
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(true);
  const [acceptedDomains, setAcceptedDomains] = useState<string[]>([]);
  
  // Password reset state
  const [isPasswordResetDialogOpen, setIsPasswordResetDialogOpen] = useState(false);
  const [passwordResetEmployee, setPasswordResetEmployee] = useState<Employee | null>(null);
  
  // ✅ NEW: Derived values from centralized state
  const activeEmployees = useMemo(() => 
    employees.filter(emp => emp.status === EmployeeStatus.ACTIVE), [employees]
  );
  
  const inactiveEmployees = useMemo(() => 
    employees.filter(emp => emp.status !== EmployeeStatus.ACTIVE), [employees]
  );
  
  const employeesByDepartment = useMemo(() => {
    const grouped = new Map<string, Employee[]>();
    employees.forEach(emp => {
      const dept = emp.department || 'Unassigned';
      if (!grouped.has(dept)) {
        grouped.set(dept, []);
      }
      grouped.get(dept)!.push(emp);
    });
    return grouped;
  }, [employees]);

  // ✅ NEW: Load employees on mount
  useEffect(() => {
    const loadEmployees = async () => {
      if (!user?.companyId || isLoadingAuth) return;
      
      try {
        setIsLoadingEmployees(true);
        setEmployeesError(null);
        
        const { data, error } = await supabase
          .from('employees')
          .select('*')
          .eq('company_id', user.companyId)
          .order('first_name')
          .order('last_name');
        
        if (error) throw error;
        
        // Transform data to Employee interface
        const transformedEmployees: Employee[] = (data || []).map((emp: any) => ({
          id: emp.id,
          name: `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || 'Unknown',
          email: emp.email || '',
          role: emp.role || UserRole.EMPLOYEE,
          department: emp.department || '',
          position: emp.position || '',
          status: emp.status || EmployeeStatus.ACTIVE,
          shift: emp.shift || '',
          residency: emp.residency || ResidencyType.RESIDENT,
          workerType: emp.worker_type || undefined, // Handle optional field
          employee_id: emp.employee_id || undefined, // Handle optional field
          is_duty_roster: emp.is_duty_roster || false, // NEW: Duty roster flag
          group_id: emp.group_id || undefined, // NEW: Group assignment
          group_name: emp.group_name || undefined // NEW: Group name
        }));
        
        setEmployees(transformedEmployees);
      } catch (error) {
        console.error('Error loading employees:', error);
        setEmployeesError('Failed to load employees');
        toast.error('Failed to load employees');
      } finally {
        setIsLoadingEmployees(false);
      }
    };

    loadEmployees();
  }, [user?.companyId, isLoadingAuth]);

  // Fetch departments and company domains on mount
  useEffect(() => {
    const fetchDepartments = async () => {
      if (!user?.companyId) return;
      
      try {
        setIsLoadingDepartments(true);
        
        const { data, error } = await supabase
          .from('departments')
          .select('*')
          .eq('company_id', user.companyId)
          .order('name');
        
        if (error) throw error;
        setDepartments(data || []);
        
        // Fetch accepted domains for email validation
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select('domains')
          .eq('id', user.companyId)
          .single();
          
        if (companyError) {
          console.warn('Could not fetch company domains:', companyError);
          setAcceptedDomains([]);
        } else {
          setAcceptedDomains((companyData as any)?.domains || []);
        }
      } catch (error) {
        console.error('Error fetching departments:', error);
        toast.error('Failed to load departments');
      } finally {
        setIsLoadingDepartments(false);
      }
    };

    // Only fetch if user is loaded and has companyId
    if (user?.companyId && !isLoadingAuth) {
      fetchDepartments();
    }
  }, [user?.companyId, isLoadingAuth]);

  // ✅ NEW: Instant state update functions
  const addEmployeeToState = useCallback((newEmployee: Employee) => {
    setEmployees(prev => [...prev, newEmployee]);
  }, []);

  const updateEmployeeInState = useCallback((updatedEmployee: Employee) => {
    setEmployees(prev => 
      prev.map(emp => emp.id === updatedEmployee.id ? updatedEmployee : emp)
    );
  }, []);

  const removeEmployeeFromState = useCallback((employeeId: string) => {
    setEmployees(prev => prev.filter(emp => emp.id !== employeeId));
  }, []);

  const updateEmployeeFieldInState = useCallback((employeeId: string, field: keyof Employee, value: any) => {
    setEmployees(prev => 
      prev.map(emp => 
        emp.id === employeeId ? { ...emp, [field]: value } : emp
      )
    );
  }, []);

  // Use useEmployees hook for data, loading state, and refetch
  const { 
    employees: employeeData, 
    isLoading, 
    refreshEmployees: refetchEmployeeListFromQueryHook 
  } = useEmployees();

  // Transform employeeData for consistency if necessary, or use directly
  // The Employee type in this file might differ slightly from useEmployees's return.
  // For now, let's assume useEmployees returns data compatible with the Employee interface here
  // or can be easily mapped.
  const users: Employee[] = useMemo(() => {
    if (!employeeData) return [];
    // Assuming employeeData is an array of objects that need mapping
    // to the local Employee interface if they are different.
    // If they are already compatible, this mapping can be simplified or removed.
    return employeeData.map((emp: any) => {
      const fName = emp.firstName || '';
      const lName = emp.lastName || '';
      const calculatedName = `${fName} ${lName}`.trim(); // Ensures empty string if both are empty

      return {
        id: emp.id,
        name: calculatedName, // Use the directly calculated name
        email: emp.email as string || '',
        role: emp.role as UserRole,
        department: emp.department as string || '',
        position: emp.position as string || '',
        status: emp.status as string || EmployeeStatus.ACTIVE, 
        shift: emp.shift as string || '',
        residency: emp.residency as string || ResidencyType.RESIDENT, 
        workerType: emp.worker_type as string || 'full_time',
        employee_id: emp.employee_id || '', // Add missing employee_id property
        is_duty_roster: emp.is_duty_roster || false, // NEW: Duty roster flag
        group_id: emp.group_id || undefined, // NEW: Group assignment
        group_name: emp.group_name || undefined // NEW: Group name
      };
    });
  }, [employeeData]);

  // Employee action handlers
  const viewEmployeeProfile = useCallback((employee: Employee) => {
    setSelectedEmployee(employee);
    setIsProfileDialogOpen(true);
  }, []);

  const handleEditUser = useCallback((employee: Employee) => {
    setSelectedEmployee(employee);
    setIsEditDialogOpen(true);
  }, []);

  const viewOvertimeHistory = useCallback((employee: Employee) => {
    setSelectedEmployee(employee);
    setIsOvertimeDialogOpen(true);
  }, []);

  const openChangeDepartmentDialog = useCallback((employee: Employee) => {
    setSelectedEmployee(employee);
    setIsChangeDeptDialogOpen(true);
  }, []);

  const openChangeStatusDialog = useCallback((employee: Employee) => {
    setSelectedEmployee(employee);
    setIsChangeStatusDialogOpen(true);
  }, []);

  const openNewUserDialog = useCallback(() => {
    setSelectedEmployee(null);
    setIsNewUserDialogOpen(true);
  }, []);

  const handlePasswordReset = useCallback((employee: Employee) => {
    setPasswordResetEmployee(employee);
    setIsPasswordResetDialogOpen(true);
  }, []);

  const onResetPassword = useCallback(async (userId: string, newPassword: string): Promise<void> => {
    // This function is not defined in the provided context, assuming it's a placeholder
    // or will be implemented elsewhere. For now, it will be a no-op.
    console.warn('Password reset functionality not yet implemented.');
  }, []);

  // ✅ UPDATED: Data update handlers with instant state updates
  const createNewUser = async (employeeData: { name: string; email?: string; department: string; position?: string; residency?: string; workerType?: string; employee_id?: string; is_duty_roster?: boolean; group_id?: string }, shiftAssignment?: { shiftId: string; fromDate: string; toDate?: string }) => {
    try {
      if (!user?.companyId) {
        toast.error('Cannot create employee: User profile is missing company association');
        return;
      }
      
      // Split the name into first and last name
      const nameParts = employeeData.name.split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ');

      // Call the database function
      const rpcParams = {
        p_first_name: firstName,
        p_last_name: lastName,
        p_email: employeeData.email || null,
        p_department: employeeData.department,
        p_position: employeeData.position || null,
        p_residency: employeeData.residency || null,
        p_worker_type: employeeData.workerType || null,
        p_employee_code: employeeData.employee_id || null,
        p_company_id: user?.companyId,
        p_created_by: user?.id,
        p_shift_id: shiftAssignment?.shiftId || null,
        p_assigned_from: shiftAssignment?.fromDate || new Date().toISOString().split('T')[0],
        p_is_duty_roster: employeeData.is_duty_roster || false,
        p_group_id: employeeData.group_id || null
      };
      
      const { data, error } = await supabase.rpc('add_employee_with_shift', rpcParams);

      if (error) throw error;

      // ✅ NEW: Create new employee object and add to state immediately
      const newEmployee: Employee = {
        id: (data as any).id,
        name: employeeData.name,
        email: employeeData.email || '',
        role: UserRole.EMPLOYEE,
        department: employeeData.department,
        position: employeeData.position || '',
        status: EmployeeStatus.ACTIVE,
        shift: shiftAssignment?.shiftId || '',
        residency: employeeData.residency || ResidencyType.RESIDENT,
        workerType: employeeData.workerType || undefined, // Handle optional field
        employee_id: employeeData.employee_id || undefined, // Handle optional field
        is_duty_roster: employeeData.is_duty_roster || false, // NEW: Duty roster flag
        group_id: employeeData.group_id || undefined, // NEW: Group assignment
        group_name: undefined // NEW: Will be populated when group data is loaded
      };

      // ✅ NEW: Add to local state immediately
      addEmployeeToState(newEmployee);
      
      setIsNewUserDialogOpen(false);
      toast.success("Employee created successfully");
    } catch (error) {
      console.error('Error creating employee:', error);
      toast.error('Failed to create employee');
    }
  };

  const saveEditedUser = async (updatedEmployee: Employee) => {
    // ✅ NEW: Store original employee for rollback
    const originalEmployee = employees.find(emp => emp.id === updatedEmployee.id);
    if (!originalEmployee) {
      toast.error('Employee not found');
      return;
    }

    try {
      // ✅ NEW: Merge only edited fields with existing data to preserve status and other fields
      const mergedEmployee: Employee = {
        ...originalEmployee, // Keep all existing fields (including status)
        name: updatedEmployee.name,
        email: updatedEmployee.email,
        department: updatedEmployee.department,
        position: updatedEmployee.position,
        residency: updatedEmployee.residency,
        workerType: updatedEmployee.workerType,
        employee_id: updatedEmployee.employee_id,
        is_duty_roster: updatedEmployee.is_duty_roster,
        group_id: updatedEmployee.group_id,
        group_name: updatedEmployee.group_name
        // Status is NOT included - it stays unchanged from originalEmployee
      };

      // ✅ NEW: Update local state with merged data (preserving status)
      updateEmployeeInState(mergedEmployee);

      // Split the name into first and last name
      const nameParts = updatedEmployee.name.split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ');

      // Call the database function
      const { error } = await supabase.rpc('edit_employee', {
        p_employee_id: updatedEmployee.id,
        p_first_name: firstName,
        p_last_name: lastName,
        p_email: updatedEmployee.email || null,
        p_department: updatedEmployee.department,
        p_position: updatedEmployee.position || null,
        p_residency: updatedEmployee.residency || null,
        p_worker_type: updatedEmployee.workerType || null,
        p_employee_code: updatedEmployee.employee_id || null,
        p_is_duty_roster: updatedEmployee.is_duty_roster !== undefined ? updatedEmployee.is_duty_roster : null,
        p_group_id: updatedEmployee.group_id || null
      });

      if (error) throw error;

      // ✅ SUCCESS: Local state already updated
      setIsEditDialogOpen(false);
      toast.success("Employee information updated successfully");
    } catch (error) {
      // ✅ ERROR: Rollback to original state
      if (originalEmployee) {
        updateEmployeeInState(originalEmployee);
      }
      console.error('Error updating employee:', error);
      toast.error('Failed to update employee information');
    }
  };

  const saveDepartmentChange = async (userId: string, newDepartment: string) => {
    // ✅ NEW: Store original department for rollback
    const originalEmployee = employees.find(emp => emp.id === userId);
    if (!originalEmployee) {
      toast.error('Employee not found');
      return;
    }

    try {
      // ✅ NEW: Update local state immediately (optimistic update)
      updateEmployeeFieldInState(userId, 'department', newDepartment);
      
      // Handle the display message appropriately based on department value
      const displayDepartment = newDepartment === "" ? "Unassigned" : newDepartment;
      
      const { error } = await (supabase as any)
        .from('employees')
        .update({ 
          department: newDepartment
        })
        .eq('id', userId);
        
      if (error) throw error;
      
      // ✅ SUCCESS: Local state already updated
      setIsChangeDeptDialogOpen(false);
      toast.success(`Department changed to ${displayDepartment} successfully`);
    } catch (error) {
      // ✅ ERROR: Rollback to original state
      if (originalEmployee) {
        updateEmployeeInState(originalEmployee);
      }
      toast.error('Failed to change department');
    }
  };

  const saveStatusChange = async (userId: string, newStatus: string) => {
    // ✅ NEW: Store original status for rollback
    const originalEmployee = employees.find(emp => emp.id === userId);
    if (!originalEmployee) {
      toast.error('Employee not found');
      return;
    }

    try {
      // ✅ NEW: Update local state immediately (optimistic update)
      updateEmployeeFieldInState(userId, 'status', newStatus);

      const { error } = await (supabase as any)
        .from('employees')
        .update({ 
          status: newStatus
        })
        .eq('id', userId);
      if (error) throw error;
      
      // ✅ SUCCESS: Local state already updated
      setIsChangeStatusDialogOpen(false);
      toast.success(`Status changed to ${newStatus} successfully`);
    } catch (error) {
      // ✅ ERROR: Rollback to original state
      if (originalEmployee) {
        updateEmployeeInState(originalEmployee);
      }
      toast.error('Failed to change status');
    }
  };

  const deleteUser = async (userId: string) => {
    try {
      if (confirm("Are you sure you want to delete this employee?")) {
        // ✅ NEW: Store original employee for rollback
        const originalEmployee = employees.find(emp => emp.id === userId);
        if (!originalEmployee) {
          toast.error('Employee not found');
          return;
        }

        // ✅ NEW: Remove from local state immediately (optimistic update)
        removeEmployeeFromState(userId);

        const { error } = await supabase
          .from('employees')
          .delete()
          .eq('id', userId);
        if (error) throw error;
        
        // ✅ SUCCESS: Local state already updated
        toast.success("Employee deleted successfully");
      }
    } catch (error) {
      // ✅ ERROR: Rollback to original state
      const originalEmployee = employees.find(emp => emp.id === userId);
      if (originalEmployee) {
        addEmployeeToState(originalEmployee);
      }
      toast.error('Failed to delete employee');
    }
  };

  // Dialog close handlers with consistent pattern
  const handleProfileDialogClose = (open: boolean) => {
    if (!open) {
      setSelectedEmployee(null);
    }
    setIsProfileDialogOpen(open);
  };

  const handleEditDialogClose = (open: boolean) => {
    if (!open) {
      setSelectedEmployee(null);
    }
    setIsEditDialogOpen(open);
  };

  const handleOvertimeDialogClose = (open: boolean) => {
    if (!open) {
      setSelectedEmployee(null);
    }
    setIsOvertimeDialogOpen(open);
  };

  const handleChangeDeptDialogClose = (open: boolean) => {
    if (!open) {
      setSelectedEmployee(null);
    }
    setIsChangeDeptDialogOpen(open);
  };

  const handleChangeStatusDialogClose = (open: boolean) => {
    if (!open) {
      setSelectedEmployee(null);
    }
    setIsChangeStatusDialogOpen(open);
  };

  const handleNewUserDialogClose = (open: boolean) => {
    // No selected employee to clear for new user dialog
    setIsNewUserDialogOpen(open);
  };

  const handlePasswordResetDialogClose = (open: boolean) => {
    if (!open) {
      setPasswordResetEmployee(null);
    }
    setIsPasswordResetDialogOpen(open);
  };

  // ✅ NEW: Duty roster utility functions
  const getDutyRosterEmployees = useCallback(() => {
    return employees.filter(emp => emp.is_duty_roster === true);
  }, [employees]);

  const getNonDutyRosterEmployees = useCallback(() => {
    return employees.filter(emp => emp.is_duty_roster === false);
  }, [employees]);

  const getEmployeesByGroup = useCallback((groupId: string) => {
    return employees.filter(emp => emp.group_id === groupId);
  }, [employees]);

  const getUngroupedDutyRosterEmployees = useCallback(() => {
    return employees.filter(emp => emp.is_duty_roster === true && !emp.group_id);
  }, [employees]);

  return {
    // ✅ NEW: Centralized state
    employees,
    activeEmployees,
    inactiveEmployees,
    employeesByDepartment,
    isLoadingEmployees,
    employeesError,
    
    // State
    activeTab,
    setActiveTab,
    isProfileDialogOpen,
    isEditDialogOpen,
    isOvertimeDialogOpen,
    isChangeDeptDialogOpen,
    isChangeStatusDialogOpen,
    selectedEmployee,
    isLoading: isLoadingEmployees || isLoadingDepartments,
    
    // Department state
    departments,
    isLoadingDepartments,
    acceptedDomains,
    
    // Password reset state
    isPasswordResetDialogOpen,
    passwordResetEmployee,
    
    // Action handlers
    viewEmployeeProfile,
    handleEditUser,
    viewOvertimeHistory,
    openChangeDepartmentDialog,
    openChangeStatusDialog,
    openNewUserDialog,
    handlePasswordReset,
    onResetPassword,
    createNewUser,
    saveEditedUser,
    saveDepartmentChange,
    saveStatusChange,
    deleteUser,
    
    // Dialog handlers
    handleProfileDialogClose,
    handleEditDialogClose,
    handleOvertimeDialogClose,
    handleChangeDeptDialogClose,
    handleChangeStatusDialogClose,
    handleNewUserDialogClose,
    handlePasswordResetDialogClose,
    isNewUserDialogOpen,
    
    // ✅ NEW: Duty roster utility functions
    getDutyRosterEmployees,
    getNonDutyRosterEmployees,
    getEmployeesByGroup,
    getUngroupedDutyRosterEmployees
  };
}
